import {
  checkApiKeyExists,
  checkApiKeyExistsForProvider,
  getApiKeyNameForProvider,
  saveApiKeyForProvider,
  saveApiKeyToEnv,
  DRISHTI_API_KEY_NAME,
} from '../utils/env.js';
import { getDefaultModelForProvider, getModelsForProvider, type Model } from '../utils/model.js';
import { getOllamaCloudModels, getOllamaModels } from '../utils/ollama.js';
import { DEFAULT_MODEL, DEFAULT_PROVIDER } from '../model/llm.js';
import { openUrl } from '../utils/open-url.js';
import {
  validateDrishtiApiKey,
  type DrishtiApiKeyValidationResult,
} from '../tools/finance/drishti-account.js';

const ONBOARDING_STATES = [
  'provider_select',
  'model_select',
  'model_input',
  'llm_key_input',
  'drishti_confirm',
  'drishti_key_input',
] as const;

export type OnboardingAppState = 'idle' | (typeof ONBOARDING_STATES)[number];

export interface OnboardingState {
  appState: OnboardingAppState;
  pendingProvider: string | null;
  pendingModels: Model[];
  pendingModel: string | null;
  portalUrl: string;
  openedPortal: boolean;
}

type ChangeListener = () => void;
type CompleteListener = (provider: string, model: string) => void;
type UrlOpener = (url: string) => boolean;
type DrishtiKeyValidator = (apiKey: string) => Promise<DrishtiApiKeyValidationResult>;

export const DRISHTI_DEVELOPER_PORTAL_URL = 'https://platform.manasija.in/developer-portal';

export class OnboardingController {
  private appStateValue: OnboardingAppState = 'idle';
  private pendingProviderValue: string | null = null;
  private pendingModelsValue: Model[] = [];
  private pendingModelValue: string | null = null;
  private providerValue = DEFAULT_PROVIDER;
  private modelValue = DEFAULT_MODEL;
  private openedPortalValue = false;
  private readonly onError: (message: string) => void;
  private readonly onComplete: CompleteListener;
  private readonly onChange?: ChangeListener;
  private readonly openPortal: UrlOpener;
  private readonly validateDrishtiKey: DrishtiKeyValidator;

  constructor(
    onError: (message: string) => void,
    onComplete: CompleteListener,
    onChange?: ChangeListener,
    openPortal: UrlOpener = openUrl,
    validateDrishtiKey: DrishtiKeyValidator = validateDrishtiApiKey,
  ) {
    this.onError = onError;
    this.onComplete = onComplete;
    this.onChange = onChange;
    this.openPortal = openPortal;
    this.validateDrishtiKey = validateDrishtiKey;
  }

  get state(): OnboardingState {
    return {
      appState: this.appStateValue,
      pendingProvider: this.pendingProviderValue,
      pendingModels: this.pendingModelsValue,
      pendingModel: this.pendingModelValue,
      portalUrl: DRISHTI_DEVELOPER_PORTAL_URL,
      openedPortal: this.openedPortalValue,
    };
  }

  isInSelectionFlow(): boolean {
    return this.appStateValue !== 'idle';
  }

  shouldStart(currentProvider: string, currentModel: string | null): boolean {
    return (
      !currentModel ||
      !checkApiKeyExistsForProvider(currentProvider) ||
      !checkApiKeyExists(DRISHTI_API_KEY_NAME)
    );
  }

  start() {
    this.pendingProviderValue = null;
    this.pendingModelsValue = [];
    this.pendingModelValue = null;
    this.openedPortalValue = false;
    this.appStateValue = 'provider_select';
    this.emitChange();
  }

  cancel() {
    this.reset();
  }

  async handleProviderSelect(providerId: string | null) {
    if (!providerId) {
      this.reset();
      return;
    }

    this.pendingProviderValue = providerId;
    if (providerId === 'openrouter') {
      this.pendingModelsValue = [];
      this.appStateValue = 'model_input';
      this.emitChange();
      return;
    }

    if (providerId === 'ollama') {
      const models = await getOllamaModels();
      this.pendingModelsValue = models.map((id) => ({ id, displayName: id }));
      this.appStateValue = 'model_select';
      this.emitChange();
      return;
    }

    if (providerId === 'ollama-cloud') {
      const models = await getOllamaCloudModels();
      this.pendingModelsValue = models.map((id) => ({ id, displayName: id }));
      this.appStateValue = 'model_select';
      this.emitChange();
      return;
    }

    this.pendingModelsValue = getModelsForProvider(providerId);
    this.appStateValue = 'model_select';
    this.emitChange();
  }

  handleModelSelect(modelId: string | null) {
    if (!modelId || !this.pendingProviderValue) {
      this.pendingProviderValue = null;
      this.pendingModelsValue = [];
      this.pendingModelValue = null;
      this.appStateValue = 'provider_select';
      this.emitChange();
      return;
    }

    const fullModelId = this.pendingProviderValue === 'ollama'
      ? `ollama:${modelId}`
      : this.pendingProviderValue === 'ollama-cloud'
        ? `ollama-cloud:${modelId}`
        : modelId;

    this.acceptModel(this.pendingProviderValue, fullModelId);
  }

  handleModelInputSubmit(modelName: string | null) {
    if (!modelName || !this.pendingProviderValue) {
      this.pendingProviderValue = null;
      this.pendingModelsValue = [];
      this.pendingModelValue = null;
      this.appStateValue = 'provider_select';
      this.emitChange();
      return;
    }

    this.acceptModel(this.pendingProviderValue, `${this.pendingProviderValue}:${modelName}`);
  }

  handleLlmKeySubmit(apiKey: string | null) {
    if (!this.pendingProviderValue || !this.pendingModelValue) {
      this.reset();
      return;
    }

    if (!apiKey) {
      this.onError('Model API key not set. Max needs a model key and a Drishti key to run financial research.');
      this.appStateValue = 'llm_key_input';
      this.emitChange();
      return;
    }

    if (!saveApiKeyForProvider(this.pendingProviderValue, apiKey)) {
      this.onError('Failed to save model API key.');
      this.appStateValue = 'llm_key_input';
      this.emitChange();
      return;
    }

    this.advanceToDrishti();
  }

  handleLlmKeyCancel() {
    if (this.appStateValue !== 'llm_key_input') return;
    this.emitChange();
  }

  handleDrishtiAction(action: 'have_key' | 'create_key' | null) {
    if (action === 'have_key') {
      this.openedPortalValue = false;
      this.appStateValue = 'drishti_key_input';
      this.emitChange();
      return;
    }

    if (action === 'create_key') {
      this.openedPortalValue = this.openPortal(DRISHTI_DEVELOPER_PORTAL_URL);
      this.appStateValue = 'drishti_key_input';
      this.emitChange();
      return;
    }

    this.emitChange();
  }

  handleDrishtiConfirm(hasKey: boolean) {
    this.handleDrishtiAction(hasKey ? 'have_key' : 'create_key');
  }

  async handleDrishtiKeySubmit(apiKey: string | null) {
    if (!apiKey) {
      this.onError(`Drishti API key not set. Get one at ${DRISHTI_DEVELOPER_PORTAL_URL}`);
      this.appStateValue = 'drishti_key_input';
      this.emitChange();
      return;
    }

    const value = apiKey.trim();
    const validation = await this.validateDrishtiKey(value);
    if (validation.status === 'invalid_key') {
      this.onError('That Drishti API key looks incorrect. Please paste a valid key.');
      this.appStateValue = 'drishti_key_input';
      this.emitChange();
      return;
    }
    if (validation.status === 'error') {
      this.onError('Could not verify the Drishti key right now. Please try again.');
      this.appStateValue = 'drishti_key_input';
      this.emitChange();
      return;
    }

    if (!saveApiKeyToEnv(DRISHTI_API_KEY_NAME, value)) {
      this.onError('Failed to save Drishti API key.');
      this.appStateValue = 'drishti_key_input';
      this.emitChange();
      return;
    }

    this.complete();
  }

  handleDrishtiKeyCancel() {
    if (this.appStateValue !== 'drishti_key_input') return;
    this.emitChange();
  }

  private acceptModel(provider: string, model: string) {
    this.providerValue = provider;
    this.modelValue = model || getDefaultModelForProvider(provider) || DEFAULT_MODEL;
    this.pendingProviderValue = provider;
    this.pendingModelValue = this.modelValue;

    const keyName = getApiKeyNameForProvider(provider);
    if (keyName && !checkApiKeyExistsForProvider(provider)) {
      this.appStateValue = 'llm_key_input';
      this.emitChange();
      return;
    }

    this.advanceToDrishti();
  }

  private advanceToDrishti() {
    if (checkApiKeyExists(DRISHTI_API_KEY_NAME)) {
      this.complete();
      return;
    }

    this.appStateValue = 'drishti_confirm';
    this.emitChange();
  }

  private complete() {
    const provider = this.providerValue;
    const model = this.modelValue;
    this.reset();
    this.onComplete(provider, model);
  }

  private reset() {
    this.appStateValue = 'idle';
    this.pendingProviderValue = null;
    this.pendingModelsValue = [];
    this.pendingModelValue = null;
    this.openedPortalValue = false;
    this.emitChange();
  }

  private emitChange() {
    this.onChange?.();
  }
}
